import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import apis from '#store/api';
import { DataApiResponse, DataCollectionApiResponse } from './types';
import { MAP_KEY } from './map';
import { PERSONS_KEY } from './persons';
import { STATUS_MAP_KEY } from './statusMap';

export const INCIDENTS_KEY = 'incidents';
export const INCIDENTS_QUERY_KEYS = {
  LIST: 'list',
  BY_ID: 'byId',
  INFINITE: 'infinite'
} as const;

export interface Incident {
  id?: string;
  name?: string;  
  address?: string;  
  isActive?: boolean;
  matchedAmount?: number;
  notifiedAmount?: number;
  startDate?: Date;
  endDate?: Date;
}

interface GetIncidentsParams {
  name?: string;
  isActive?: boolean;
  skip?: number;
  take: number;  
}

interface GetIncidentByIdParams {
  id: string;
  includeVictimsData?: boolean;
}

interface CreateIncidentParams {
  incident: Omit<Incident, 'id' | 'matchedAmount' | 'notifiedAmount'>;
}

interface UpdateIncidentParams {
  id: string;
  incident: Incident;
}

// Fetch incidents list
export const useIncidents = (params: GetIncidentsParams) => {
  return useQuery({
    queryKey: [INCIDENTS_KEY, INCIDENTS_QUERY_KEYS.LIST, params],
    queryFn: async () => {
      const response = await apis.incidents.getIncidentsList({ params });
      return response.data as DataCollectionApiResponse<Incident>;
    },
  });
};

// Fetch incidents with infinite loading
export const useInfiniteIncidents = (params: Omit<GetIncidentsParams, 'skip'>) => {
  return useInfiniteQuery<
    DataCollectionApiResponse<Incident>,
    Error,
    InfiniteData<DataCollectionApiResponse<Incident>>,
    [string, string, GetIncidentsParams],
    number
  >({
    queryKey: [INCIDENTS_KEY, INCIDENTS_QUERY_KEYS.INFINITE, params],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const response = await apis.incidents.getIncidentsList({ 
        params: {
          ...params,
          isActive: params.isActive ?? true,
          skip: pageParam * params.take
        }
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.items.length < params.take) return undefined;
      return allPages.length;
    }
  });
};

// Fetch incident by id
export const useIncidentById = (params: GetIncidentByIdParams) => {
  return useQuery({
    queryKey: [INCIDENTS_KEY, INCIDENTS_QUERY_KEYS.BY_ID, params],
    queryFn: async () => {
      const response = await apis.incidents.getIncidentById({ data: params });
      return response.data as DataApiResponse<Incident>;
    },
    enabled: !!params.id,
  });
};

// Create incident
export const useCreateIncident = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: CreateIncidentParams) => {
      const response = await apis.incidents.createIncident({ data: params });
      return response.data as DataApiResponse<Incident>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INCIDENTS_KEY] });
    },
  });
};

// Update incident
export const useUpdateIncident = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: UpdateIncidentParams) => {
      const response = await apis.incidents.updateIncident({ data: params });
      return response.data as DataApiResponse<Incident>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INCIDENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MAP_KEY] });
      queryClient.invalidateQueries({ queryKey: [PERSONS_KEY]});
      queryClient.invalidateQueries({ queryKey: [STATUS_MAP_KEY] });
    },
  });
};

// Delete incident
export const useDeleteIncident = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apis.incidents.deleteIncident({ data: { id } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INCIDENTS_KEY] });
    },
  });
};